import { useState, useEffect } from "react";
import { View, Text, ScrollView, Pressable, Alert, Image } from "react-native";
import { SafeAreaView } from "react-native-safe-area-context";
import { Ionicons } from "@expo/vector-icons";
import { useAuth } from "@/providers/AuthProvider";
import { useTheme, useThemeColors } from "@/providers/ThemeProvider";
import { useRouter } from "expo-router";
import { supabase } from "@/lib/supabase/client";
import { FamilyWeekend } from "@/types";
import * as WebBrowser from "expo-web-browser";
import Constants from "expo-constants";

/** Base URL for web pages hosted on EAS */
const WEB_BASE_URL = "https://staatsen-app.expo.app";

/**
 * Role badge component
 */
interface RoleBadgeProps {
  role: "super_admin" | "familie_admin" | "user";
}

const RoleBadge = ({ role }: RoleBadgeProps) => {
  const config = {
    super_admin: {
      label: "Super Admin",
      icon: "shield-checkmark" as const,
      bgColor: "#7C3AED",
      textColor: "#FFFFFF",
    },
    familie_admin: {
      label: "Familie Admin",
      icon: "shield" as const,
      bgColor: "#D97706",
      textColor: "#FFFFFF",
    },
    user: {
      label: "Familie Lid",
      icon: "person" as const,
      bgColor: "#10B981",
      textColor: "#FFFFFF",
    },
  };

  const { label, icon, bgColor, textColor } = config[role];

  return (
    <View
      className="flex-row items-center px-3 py-1.5 rounded-full"
      style={{ backgroundColor: bgColor }}
    >
      <Ionicons name={icon} size={14} color={textColor} />
      <Text className="text-xs font-semibold ml-1" style={{ color: textColor }}>
        {label}
      </Text>
    </View>
  );
};

/**
 * Profile menu item component with dark mode support
 */
interface MenuItemProps {
  icon: keyof typeof Ionicons.glyphMap;
  iconColor: string;
  iconBgColor: string;
  title: string;
  subtitle?: string;
  onPress: () => void;
  showBorder?: boolean;
  rightElement?: React.ReactNode;
  disabled?: boolean;
}

const MenuItem = ({
  icon,
  iconColor,
  iconBgColor,
  title,
  subtitle,
  onPress,
  showBorder = true,
  rightElement,
  disabled = false,
}: MenuItemProps) => {
  const colors = useThemeColors();

  return (
    <Pressable
      onPress={disabled ? undefined : onPress}
      disabled={disabled}
      className="flex-row items-center p-4"
      style={{
        borderBottomWidth: showBorder ? 1 : 0,
        borderBottomColor: colors.border,
        opacity: disabled ? 0.5 : 1,
      }}
      accessibilityRole="button"
      accessibilityLabel={title}
      accessibilityState={{ disabled }}
    >
      <View
        className="w-10 h-10 rounded-full items-center justify-center mr-3"
        style={{ backgroundColor: iconBgColor }}
      >
        <Ionicons name={icon} size={20} color={iconColor} />
      </View>
      <View className="flex-1">
        <Text className="font-medium" style={{ color: disabled ? colors.textTertiary : colors.text }}>
          {title}
        </Text>
        {subtitle && (
          <Text className="text-xs mt-0.5" style={{ color: colors.textTertiary }}>
            {disabled ? "Niet beschikbaar voor kind gebruiker" : subtitle}
          </Text>
        )}
      </View>
      {rightElement || (
        <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
      )}
    </Pressable>
  );
};

/**
 * Past weekend card component with dark mode
 */
interface PastWeekendCardProps {
  weekend: FamilyWeekend;
  onPress: () => void;
}

const PastWeekendCard = ({ weekend, onPress }: PastWeekendCardProps) => {
  const colors = useThemeColors();

  return (
    <Pressable
      onPress={onPress}
      className="rounded-xl p-4 mr-3"
      style={{
        width: 160,
        backgroundColor: colors.surface,
        borderWidth: 1,
        borderColor: colors.border,
      }}
      accessibilityRole="button"
      accessibilityLabel={`Bekijk ${weekend.name}`}
    >
      <View
        className="w-10 h-10 rounded-full items-center justify-center mb-2"
        style={{ backgroundColor: colors.primaryLight }}
      >
        <Text className="text-lg">🎉</Text>
      </View>
      <Text className="font-semibold" numberOfLines={1} style={{ color: colors.text }}>
        {weekend.name}
      </Text>
      <Text className="text-sm mt-0.5" style={{ color: colors.textSecondary }}>
        {weekend.year}
      </Text>
      {weekend.theme && (
        <Text
          className="text-xs mt-1"
          numberOfLines={1}
          style={{ color: colors.primary }}
        >
          {weekend.theme}
        </Text>
      )}
    </Pressable>
  );
};

/**
 * Profile Screen - User settings and past weekends
 */
export default function ProfileScreen() {
  const router = useRouter();
  const { profile, activeProfile, userTeam, activeWeekend, roles, isSwitched, signOut, isLoading } = useAuth();
  const { isDark, themeMode, setThemeMode } = useTheme();
  const colors = useThemeColors();
  const [pastWeekends, setPastWeekends] = useState<FamilyWeekend[]>([]);

  // Use activeProfile (could be dependent) for display
  const displayProfile = activeProfile || profile;

  // Determine highest role to display (only for main user, dependents are "user")
  const displayRole = isSwitched
    ? "user"
    : roles.isSuperAdmin
    ? "super_admin"
    : roles.isFamilieAdmin
    ? "familie_admin"
    : "user";

  /**
   * Fetch past weekends (only finalized ones)
   */
  useEffect(() => {
    const fetchPastWeekends = async () => {
      const { data, error } = await supabase
        .from("family_weekends")
        .select("*")
        .eq("is_finalized", true)
        .order("year", { ascending: false })
        .limit(10);

      if (!error && data) {
        setPastWeekends(data);
      }
    };

    fetchPastWeekends();
  }, []);

  /**
   * Handle sign out with confirmation
   */
  const handleSignOut = () => {
    Alert.alert("Uitloggen", "Weet je zeker dat je wilt uitloggen?", [
      { text: "Annuleer", style: "cancel" },
      {
        text: "Uitloggen",
        style: "destructive",
        onPress: async () => {
          try {
            await signOut();
          } catch (error) {
            Alert.alert("Fout", "Er ging iets mis bij het uitloggen");
          }
        },
      },
    ]);
  };

  /**
   * Handle edit profile
   */
  const handleEditProfile = () => {
    router.push("/(tabs)/profile/edit");
  };

  /**
   * Handle account settings
   */
  const handleAccountSettings = () => {
    router.push("/(tabs)/profile/account");
  };

  /**
   * Handle notifications settings
   */
  const handleNotifications = () => {
    router.push("/(tabs)/profile/notifications");
  };

  /**
   * Handle theme toggle
   */
  const handleThemeToggle = () => {
    Alert.alert("Thema", "Kies een thema", [
      {
        text: "Licht",
        onPress: () => setThemeMode("light"),
      },
      {
        text: "Donker",
        onPress: () => setThemeMode("dark"),
      },
      {
        text: "Systeem",
        onPress: () => setThemeMode("system"),
      },
      { text: "Annuleer", style: "cancel" },
    ]);
  };

  /**
   * Open web page in browser
   */
  const handleOpenWebPage = async (path: string) => {
    try {
      await WebBrowser.openBrowserAsync(`${WEB_BASE_URL}${path}`);
    } catch (error) {
      console.error("Error opening web page:", error);
      Alert.alert("Fout", "Kon de pagina niet openen");
    }
  };

  /**
   * Handle view past weekend
   */
  const handleViewPastWeekend = (weekend: FamilyWeekend) => {
    router.push(`/(tabs)/profile/weekend/${weekend.id}`);
  };

  /**
   * Handle view all past weekends
   */
  const handleViewAllWeekends = () => {
    router.push("/(tabs)/profile/weekends");
  };

  const initials = displayProfile?.full_name
    ?.split(" ")
    .map((n) => n.charAt(0))
    .join("")
    .toUpperCase()
    .slice(0, 2) || "?";

  const themeLabel =
    themeMode === "system"
      ? "Systeem"
      : themeMode === "dark"
      ? "Donker"
      : "Licht";

  return (
    <SafeAreaView
      className="flex-1"
      style={{ backgroundColor: colors.background }}
      edges={["top"]}
    >
      <ScrollView className="flex-1" showsVerticalScrollIndicator={false}>
        {/* Header */}
        <View
          className="px-4 py-4 border-b"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          <Text className="text-2xl font-bold" style={{ color: colors.text }}>
            Profiel
          </Text>
        </View>

        {/* Profile Card */}
        <View
          className="items-center py-6 border-b"
          style={{ backgroundColor: colors.surface, borderColor: colors.border }}
        >
          {/* Avatar */}
          <Pressable onPress={handleEditProfile}>
            <View
              className="w-24 h-24 rounded-full items-center justify-center mb-4 border-4 overflow-hidden"
              style={{
                backgroundColor: colors.primaryLight,
                borderColor: colors.primary,
              }}
            >
              {displayProfile?.avatar_url ? (
                <Image
                  source={{ uri: displayProfile.avatar_url }}
                  className="w-full h-full"
                  resizeMode="cover"
                />
              ) : (
                <Text className="text-4xl font-bold" style={{ color: colors.primary }}>
                  {initials}
                </Text>
              )}
            </View>
          </Pressable>

          {/* Name */}
          <Text className="text-xl font-bold" style={{ color: colors.text }}>
            {displayProfile?.full_name || "Gebruiker"}
          </Text>

          {/* Dependent Badge */}
          {displayProfile?.is_dependent && (
            <View
              className="mt-1 px-3 py-1 rounded-full"
              style={{ backgroundColor: "#3B82F620" }}
            >
              <Text className="text-xs font-medium" style={{ color: "#3B82F6" }}>
                👶 Kind
              </Text>
            </View>
          )}

          {/* Team Badge */}
          {userTeam && (
            <View className="flex-row items-center mt-1">
              <View
                className="w-3 h-3 rounded-full mr-2"
                style={{ backgroundColor: userTeam.color || "#7C3AED" }}
              />
              <Text className="font-medium" style={{ color: colors.primary }}>
                {userTeam.name}
              </Text>
            </View>
          )}

          {/* Email */}
          {displayProfile?.email && (
            <Text className="text-sm mt-1" style={{ color: colors.textSecondary }}>
              {displayProfile.email}
            </Text>
          )}

          {/* Role Badge */}
          <View className="mt-3">
            <RoleBadge role={displayRole} />
          </View>

          {/* Active Weekend Badge */}
          {activeWeekend && (
            <View
              className="mt-3 px-4 py-1.5 rounded-full"
              style={{ backgroundColor: colors.primaryLight }}
            >
              <Text className="text-sm font-medium" style={{ color: colors.primary }}>
                {activeWeekend.name} {activeWeekend.year}
              </Text>
            </View>
          )}
        </View>

        {/* Past Weekends Section */}
        {pastWeekends.length > 0 && (
          <View className="mt-4">
            <View className="flex-row items-center justify-between px-4 mb-3">
              <Text
                className="text-sm font-semibold uppercase"
                style={{ color: colors.textSecondary }}
              >
                Vorige Weekenden
              </Text>
              <Pressable onPress={handleViewAllWeekends}>
                <Text className="text-sm font-medium" style={{ color: colors.primary }}>
                  Bekijk alle
                </Text>
              </Pressable>
            </View>
            <ScrollView
              horizontal
              showsHorizontalScrollIndicator={false}
              contentContainerClassName="px-4 pb-2"
            >
              {pastWeekends.map((weekend) => (
                <PastWeekendCard
                  key={weekend.id}
                  weekend={weekend}
                  onPress={() => handleViewPastWeekend(weekend)}
                />
              ))}
            </ScrollView>
          </View>
        )}

        {/* Profile Settings Section */}
        <View className="px-4 mt-4">
          <Text
            className="text-sm font-semibold uppercase mb-3"
            style={{ color: colors.textSecondary }}
          >
            Profiel
          </Text>

          <View
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <MenuItem
              icon="person-outline"
              iconColor="#2563EB"
              iconBgColor={isDark ? "#1E3A5F" : "#DBEAFE"}
              title="Profiel Bewerken"
              subtitle="Naam, foto, telefoonnummer"
              onPress={handleEditProfile}
            />
            <MenuItem
              icon="key-outline"
              iconColor="#7C3AED"
              iconBgColor={isDark ? "#4C1D95" : "#EDE9FE"}
              title="Account"
              subtitle="E-mail en wachtwoord"
              onPress={handleAccountSettings}
              disabled={isSwitched}
            />
            <MenuItem
              icon="notifications-outline"
              iconColor="#F59E0B"
              iconBgColor={isDark ? "#78350F" : "#FEF3C7"}
              title="Notificaties"
              subtitle="Push en in-app meldingen"
              onPress={handleNotifications}
              showBorder={false}
              disabled={isSwitched}
            />
          </View>
        </View>

        {/* App Settings Section */}
        <View className="px-4 mt-4">
          <Text
            className="text-sm font-semibold uppercase mb-3"
            style={{ color: colors.textSecondary }}
          >
            App
          </Text>

          <View
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <MenuItem
              icon={isDark ? "moon" : "sunny"}
              iconColor={isDark ? "#A78BFA" : "#F59E0B"}
              iconBgColor={isDark ? "#4C1D95" : "#FEF3C7"}
              title="Thema"
              subtitle={themeLabel}
              onPress={handleThemeToggle}
              rightElement={
                <View className="flex-row items-center">
                  <Text className="text-sm mr-2" style={{ color: colors.textSecondary }}>
                    {themeLabel}
                  </Text>
                  <Ionicons name="chevron-forward" size={20} color={colors.textTertiary} />
                </View>
              }
              showBorder={false}
            />
          </View>
        </View>

        {/* Information Section */}
        <View className="px-4 mt-4">
          <Text
            className="text-sm font-semibold uppercase mb-3"
            style={{ color: colors.textSecondary }}
          >
            Informatie
          </Text>

          <View
            className="rounded-2xl overflow-hidden"
            style={{ backgroundColor: colors.surface, borderWidth: 1, borderColor: colors.border }}
          >
            <MenuItem
              icon="shield-checkmark-outline"
              iconColor="#7C3AED"
              iconBgColor={isDark ? "#4C1D95" : "#EDE9FE"}
              title="Privacybeleid"
              subtitle="Hoe we omgaan met je gegevens"
              onPress={() => handleOpenWebPage("/privacy-policy")}
            />
            <MenuItem
              icon="document-text-outline"
              iconColor="#3B82F6"
              iconBgColor={isDark ? "#1E3A5F" : "#DBEAFE"}
              title="Gebruiksvoorwaarden"
              subtitle="Regels en voorwaarden"
              onPress={() => handleOpenWebPage("/terms")}
            />
            <MenuItem
              icon="help-circle-outline"
              iconColor="#10B981"
              iconBgColor={isDark ? "#064E3B" : "#D1FAE5"}
              title="Support"
              subtitle="Hulp en veelgestelde vragen"
              onPress={() => handleOpenWebPage("/support")}
              showBorder={false}
            />
          </View>
        </View>

        {/* Sign Out */}
        <View className="px-4 mt-6 mb-8">
          <Pressable
            onPress={handleSignOut}
            disabled={isLoading}
            className="rounded-2xl p-4 flex-row items-center justify-center"
            style={{
              backgroundColor: colors.errorLight,
              borderWidth: 1,
              borderColor: colors.error,
            }}
            accessibilityRole="button"
            accessibilityLabel="Uitloggen"
          >
            <Ionicons name="log-out-outline" size={20} color="#EF4444" />
            <Text className="font-semibold ml-2 text-red-500">
              {isLoading ? "Laden..." : "Uitloggen"}
            </Text>
          </Pressable>
        </View>

        {/* App Version */}
        <View className="items-center py-4">
          <Text className="text-xs" style={{ color: colors.textTertiary }}>
            Versie {Constants.expoConfig?.version }
          </Text>
        </View>

        <View className="h-4" />
      </ScrollView>
    </SafeAreaView>
  );
}
